import {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Badge,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import NewsModal from '../shared/NewsModal';
import ExchangeSelect from 'components/exchangeSelect/ExchangeSelect';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {useNewsExchange} from '../shared/useNewsExchange';
import {useDebounced} from '../shared/useDebounced';
import {actionError, loadError, unionError} from '../shared/errors';
import {ActionButton, HowItWorks, NO_PERMISSION, Tip} from '../shared/ui';
import AddWebsiteModal from './AddWebsiteModal';
import UnplacedArticlesModal from './UnplacedArticlesModal';
import {
  DELETE_NEWS_SOURCE,
  DETACH_NEWS_SOURCE_COVERAGE,
  GET_NEWS_SOURCES,
  GET_NEWS_SOURCE_EXCHANGE_HEALTH,
  RETRY_NEWS_SOURCE,
  UPDATE_NEWS_SOURCE,
} from './resolvers';
import {
  CONFIRM,
  HOW_IT_WORKS,
  LIST,
  STATUS,
  TOAST,
  TRY_AGAIN_TIP,
} from './copy';
import {
  coverageFor,
  exchangeCode,
  exchangeDefaults,
  fromNow,
  otherExchanges,
  replaceCoverage,
  sourceStatus,
} from './sourceHelpers';
import styles from '../shared/news.module.scss';

export const SOURCES_SUB_TAB = 'news-sources';

// A website's status word, and when something is wrong, what and what to do.
export const StatusCell = ({source, onRetry, retrying, canRetry}) => {
  const status = sourceStatus(source);
  const word = STATUS[status.key];
  return (
    <div>
      <Badge status={word.badge} text={word.label} />
      {status.message && status.key !== 'working' ? (
        <p className={styles.reason}>{status.message}</p>
      ) : null}
      {status.action && onRetry ? (
        <ActionButton
          size="small"
          allowed={canRetry}
          tip={TRY_AGAIN_TIP}
          loading={retrying}
          onClick={onRetry}>
          {status.action}
        </ActionButton>
      ) : null}
    </div>
  );
};

// Reads the website now; used by the list and a website's page.
export const useRetry = onDone => {
  const [retrySource] = useMutation(RETRY_NEWS_SOURCE, {
    onError: actionError('read this website'),
  });
  const [retrying, setRetrying] = useState(null);
  const retry = async source => {
    setRetrying(source.id);
    const {data} = await retrySource({variables: {sourceId: source.id}});
    setRetrying(null);
    const result = data?.retryNewsSource;
    if (!result) return;
    if (result.__typename === 'Error' || result.error) {
      return unionError('read this website', {
        message: result.message || result.error,
      });
    }
    toast.success(TOAST.retried(source.name, result.ingested ?? 0));
    onDone();
  };
  return {retry, retrying};
};

const NewsSources = () => {
  const navigate = useNavigate();
  const {exchange, exchangeId, selectExchange, waiting} = useNewsExchange();
  const [modal, setModal] = useState(null);
  const ops = useSelector(state =>
    makeSelectSubTabOperations(state, NavTabsEnum.News, SOURCES_SUB_TAB),
  );
  const x = exchangeCode(exchange);

  const [searchText, setSearchText] = useState('');
  const search = useDebounced(searchText.trim()) || undefined;

  // About 30 websites per exchange, so one page of 100 holds them all and the
  // table pages through it.
  const sourcesQuery = useQuery(GET_NEWS_SOURCES, {
    variables: {exchangeId, search, limit: 100},
    skip: !exchangeId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });
  const healthQuery = useQuery(GET_NEWS_SOURCE_EXCHANGE_HEALTH, {
    variables: {exchangeId},
    skip: !exchangeId,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const refresh = () => {
    sourcesQuery.refetch();
    healthQuery.refetch();
  };

  const [updateSource] = useMutation(UPDATE_NEWS_SOURCE, {
    onError: actionError('change this website'),
  });
  const [detach] = useMutation(DETACH_NEWS_SOURCE_COVERAGE, {
    onError: actionError('remove this website'),
  });
  const [deleteSource] = useMutation(DELETE_NEWS_SOURCE, {
    onError: actionError('delete this website'),
  });
  const {retry, retrying} = useRetry(refresh);

  const listResponse = sourcesQuery.data?.newsSources;
  const sources =
    listResponse?.__typename === 'RegisteredNewsSourceList'
      ? listResponse.data.filter(Boolean)
      : [];
  const listFailed = listResponse?.__typename === 'Error' || sourcesQuery.error;
  const health =
    healthQuery.data?.newsSourceExchangeHealth?.__typename ===
    'NewsSourceExchangeHealth'
      ? healthQuery.data.newsSourceExchangeHealth
      : null;

  // The summary and Add a website's defaults describe the whole exchange, so
  // they keep the last list loaded without a search.
  const everyWebsite = useRef({exchangeId: null, list: []});
  if (!search && !sourcesQuery.loading && listResponse) {
    everyWebsite.current = {exchangeId, list: sources};
  }
  const allSources =
    everyWebsite.current.exchangeId === exchangeId
      ? everyWebsite.current.list
      : sources;
  const statuses = allSources.map(source => sourceStatus(source).key);
  const working = statuses.filter(key => key === 'working').length;
  const trouble = statuses.filter(key => key === 'trouble').length;

  // Every change reports back in words, then reloads the list.
  const run = async (promise, field, message) => {
    const {data} = await promise;
    const result = data?.[field];
    if (!result) return;
    if (result.__typename === 'Error') {
      return unionError('change this website', result);
    }
    toast.success(message);
    refresh();
  };

  const setActive = (source, enabled) => {
    const apply = () =>
      run(
        updateSource({
          variables: {
            sourceId: source.id,
            data: {coverage: replaceCoverage(source, exchangeId, {enabled})},
          },
        }),
        'updateNewsSource',
        enabled ? TOAST.on(source.name, x) : TOAST.off(source.name, x),
      );
    if (enabled) return apply();
    Modal.confirm({
      title: CONFIRM.offTitle(source.name, x),
      content: CONFIRM.off(source.name, x),
      okText: 'Turn off',
      onOk: apply,
    });
  };

  const menuFor = source => {
    const others = otherExchanges(source, exchangeId);
    const paused = source.enabled === false;
    return {
      items: [
        {
          key: 'pause',
          disabled: !ops?.update,
          label: paused ? 'Resume' : 'Pause',
        },
        others.length
          ? {key: 'remove', disabled: !ops?.update, label: LIST.removeFrom(x)}
          : null,
        {
          key: 'delete',
          disabled: !ops?.delete,
          danger: true,
          label: LIST.delete,
        },
      ].filter(Boolean),
      onClick: ({key}) => {
        if (key === 'pause') {
          const pause = () =>
            run(
              updateSource({
                variables: {sourceId: source.id, data: {enabled: paused}},
              }),
              'updateNewsSource',
              paused ? TOAST.resumed(source.name) : TOAST.paused(source.name),
            );
          if (paused) return pause();
          return Modal.confirm({
            title: CONFIRM.pauseTitle(source.name),
            content: CONFIRM.pause(source.name),
            okText: 'Pause',
            onOk: pause,
          });
        }
        if (key === 'remove') {
          return Modal.confirm({
            title: CONFIRM.removeTitle(source.name, x),
            content: CONFIRM.remove(source.name, x, others),
            okText: 'Remove',
            okButtonProps: {danger: true},
            onOk: () =>
              run(
                detach({variables: {sourceId: source.id, exchangeId}}),
                'detachNewsSourceCoverage',
                TOAST.removed(source.name, x),
              ),
          });
        }
        Modal.confirm({
          title: CONFIRM.deleteTitle(source.name),
          content: CONFIRM.delete(source.name, [x, ...others]),
          okText: 'Delete',
          okButtonProps: {danger: true},
          onOk: () =>
            run(
              deleteSource({variables: {sourceId: source.id}}),
              'deleteNewsSource',
              TOAST.deleted(source.name),
            ),
        });
      },
    };
  };

  const columns = [
    {
      title: <Tip label={LIST.website} tip={LIST.websiteTip} />,
      key: 'website',
      render: (_, source) => (
        <Button
          type="link"
          className={styles.linkButton}
          onClick={() => navigate(`/dashboard/news/sources/${source.id}`)}>
          {source.name}
        </Button>
      ),
    },
    {
      title: <Tip label={LIST.status} tip={LIST.statusTip} />,
      key: 'status',
      width: 280,
      render: (_, source) => (
        <StatusCell
          source={source}
          canRetry={ops?.update}
          retrying={retrying === source.id}
          onRetry={() => retry(source)}
        />
      ),
    },
    {
      title: <Tip label={LIST.lastChecked} tip={LIST.lastCheckedTip} />,
      key: 'lastChecked',
      render: (_, source) =>
        fromNow(source.health?.lastSuccessAt) || LIST.noneYet,
    },
    {
      title: <Tip label={LIST.thisWeek} tip={LIST.thisWeekTip} />,
      key: 'thisWeek',
      render: (_, source) => source.health?.itemsLast7d ?? 0,
    },
    {
      title: <Tip label={LIST.alsoFeeds} tip={LIST.alsoFeedsTip} />,
      key: 'also',
      render: (_, source) => {
        const others = otherExchanges(source, exchangeId);
        return others.length ? others.map(o => <Tag key={o}>{o}</Tag>) : '—';
      },
    },
    {
      title: <Tip label={LIST.activeOn(x)} tip={LIST.activeOnTip(x)} />,
      key: 'active',
      render: (_, source) => (
        <Switch
          size="small"
          checked={coverageFor(source, exchangeId)?.enabled !== false}
          disabled={!ops?.update}
          title={ops?.update ? undefined : NO_PERMISSION}
          onChange={checked => setActive(source, checked)}
        />
      ),
    },
    {
      title: '',
      key: 'menu',
      width: 48,
      render: (_, source) => (
        <Dropdown menu={menuFor(source)} trigger={['click']}>
          <Button type="text" aria-label={LIST.more} icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const addButton = (
    <ActionButton
      type="primary"
      icon={<PlusOutlined />}
      allowed={ops?.create}
      tip={LIST.addTip(x)}
      disabled={!exchangeId}
      onClick={() => setModal('add')}>
      {LIST.add}
    </ActionButton>
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <span className={styles.tipLabel}>
          <Tip label={LIST.exchange} tip={LIST.exchangeTip} />
          <ExchangeSelect
            className={styles.exchangeSelect}
            value={exchangeId}
            onChange={selectExchange}
            placeholder="Pick an exchange"
          />
        </span>
        {addButton}
      </div>

      {waiting ? (
        <Spin className={styles.spinner} />
      ) : !exchangeId ? (
        <Empty description={LIST.pickExchange} />
      ) : (
        <>
          <HowItWorks id="sources">{HOW_IT_WORKS(x)}</HowItWorks>

          {allSources.length ? (
            <div className={styles.summary}>
              <strong>{x}</strong>
              <span>{LIST.websites(allSources.length)}</span>
              <span>{LIST.working(working)}</span>
              {trouble ? <span>{LIST.trouble(trouble)}</span> : null}
              {health ? (
                <Tip
                  label={LIST.thisWeekSummary(health.itemsLast7d ?? 0)}
                  tip={LIST.thisWeekSummaryTip}
                />
              ) : null}
              {health?.unrouted ? (
                <Button
                  type="link"
                  className={styles.linkButton}
                  icon={<WarningOutlined />}
                  title={LIST.unplacedTip}
                  onClick={() => setModal('unplaced')}>
                  {LIST.unplaced(health.unrouted)}
                </Button>
              ) : null}
            </div>
          ) : null}

          {listFailed ? (
            <Alert
              type="error"
              showIcon
              message={loadError(
                'the news websites',
                listResponse?.__typename === 'Error'
                  ? listResponse
                  : sourcesQuery.error,
              )}
            />
          ) : null}

          {allSources.length || search ? (
            <Tooltip title={LIST.searchTip}>
              <Input
                allowClear
                className={styles.searchBox}
                prefix={<SearchOutlined />}
                placeholder={LIST.search}
                aria-label={LIST.search}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </Tooltip>
          ) : null}

          {!sourcesQuery.loading && !listFailed && !sources.length ? (
            search ? (
              <Empty description={LIST.emptySearch} />
            ) : (
              <Empty description={LIST.empty(x)}>{addButton}</Empty>
            )
          ) : (
            <Table
              className="antd-table"
              columns={columns}
              dataSource={sources}
              loading={sourcesQuery.loading}
              rowKey="id"
              pagination={{pageSize: 20, hideOnSinglePage: true}}
            />
          )}
        </>
      )}

      <NewsModal
        isOpen={modal === 'add'}
        onClose={() => setModal(null)}
        width={720}>
        <AddWebsiteModal
          exchange={exchange}
          defaults={exchangeDefaults(exchange, allSources)}
          canCreate={ops?.create}
          onSaved={() => {
            setModal(null);
            refresh();
          }}
        />
      </NewsModal>
      <NewsModal
        isOpen={modal === 'unplaced'}
        onClose={() => setModal(null)}
        width={900}>
        <UnplacedArticlesModal canUpdate={ops?.update} onChanged={refresh} />
      </NewsModal>
    </div>
  );
};

export default NewsSources;
